// React
import PropTypes from "prop-types";
import React, {
  Children,
  cloneElement,
  forwardRef,
  isValidElement,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
// Helpers
import { has, noop } from "@mefisto/utils";
// Framework
import { EntityPropType } from "model/utils";
import { usePortal } from "stack";
import { useTheme } from "theme";
import { CoverSpinner, Dialog, makeStyles, useMediaQuery } from "ui";
// Components
import ModelDialogTitle from "../ModelDialogTitle";

////////////////////////////////////////////////////
/// Styles
////////////////////////////////////////////////////

const useStyles = makeStyles(() => ({
  paper: {
    minHeight: 200,
  },
}));

////////////////////////////////////////////////////
/// Component
////////////////////////////////////////////////////

const getTypeAction = (type) => {
  switch (type.displayName) {
    case "ModelDialogActionCreate":
      return "create";
    case "ModelDialogActionUpdate":
      return "update";
    case "ModelDialogActionDelete":
      return "delete";
    case "ModelDialogActionRemove":
      return "remove";

    default:
      return null;
  }
};

const ModelDialog = forwardRef(
  (
    {
      title: defaultTitle,
      entity,
      schema,
      dialogProps,
      loading,
      onFinish = noop,
      onClose = noop,
      children,
    },
    ref
  ) => {
    // Framework
    const { log } = usePortal();
    // Styles
    const classes = useStyles();
    const { breakpoints } = useTheme();
    const sm = useMediaQuery(breakpoints.down("sm"));
    // State
    const [action, setAction] = useState(null);
    const [options, setOptions] = useState(null);
    const [open, setOpen] = useState(false);
    const [title, setTitle] = useState(defaultTitle);
    const [attempt, setAttempt] = useState(0);
    const [keepOpen, setKeepOpen] = useState(false);
    // Memo
    const actions = useMemo(() => {
      let actions = {};
      Children.forEach(children, (child) => {
        // noinspection JSCheckFunctionSignatures
        if (isValidElement(child)) {
          const { props = {}, type } = child;
          // Each dialog child must have an action so when we
          // open the dialog we know which action to show.
          // Either use action defined in the child or guess it from type.
          const action = props.action ?? getTypeAction(type);
          if (action) {
            // noinspection JSCheckFunctionSignatures
            actions[action] = cloneElement(
              child,
              {
                action,
                // Entity and schema might be defined for the whole dialog, so
                // we don't need to define it in each action.
                entity: props.entity ?? entity,
                schema: props.schema ?? schema,
              },
              props.children
            );
          }
        }
      });
      return actions;
    }, [children, entity, schema]);
    // Handlers
    const handleFinish = useCallback(
      (payload) => {
        if (keepOpen) {
          setAttempt((attempt) => attempt + 1);
        } else {
          setOpen(false);
        }
        onFinish({ action, ...payload });
      },
      [onFinish, action, keepOpen]
    );
    const handleClose = useCallback(
      (_, reason) => {
        if (reason === "backdropClick") {
          return;
        }
        setOpen(false);
        onClose({ action });
      },
      [onClose, action]
    );
    const handleKeepOpen = useCallback((event) => {
      setKeepOpen(event.target.checked);
    }, []);
    // Ref
    useImperativeHandle(ref, () => ({
      /**
       * Opens dialog in `create` action
       * @param action {string} Current dialog action
       * @param options {object} Dialog options
       */
      open(
        action,
        options = { title: null, keepOpen: false, optimisticUI: true }
      ) {
        if (!has(actions, action)) {
          log.error(
            `Given action [${action}] is not provided by the model dialog.`
          );
          return;
        }
        setAction(action);
        if (options.title) {
          setTitle(options.title);
        }
        setOptions({
          ...options,
          keepOpen: !!options.keepOpen && action === "create",
        });
        setOpen(true);
      },
      close() {
        setOpen(false);
      },
    }));
    useEffect(() => {
      if (defaultTitle) {
        setTitle(defaultTitle);
      }
    }, [defaultTitle]);
    // Render
    return (
      <Dialog
        fullWidth
        fullScreen={options?.fullScreen ?? sm}
        onClose={handleClose}
        open={open}
        classes={{ paper: classes.paper }}
        {...dialogProps}
      >
        {loading && <CoverSpinner size="small" />}
        <ModelDialogTitle title={title} onClose={handleClose} />
        {isValidElement(actions[action]) &&
          cloneElement(actions[action], {
            key: attempt,
            __options: options,
            __display: {
              action,
              keepOpen,
              onClose: handleClose,
              onFinish: handleFinish,
              onKeepOpen: handleKeepOpen,
            },
          })}
      </Dialog>
    );
  }
);

ModelDialog.propTypes = {
  /**
   * Dialog entity class.
   * All the actions will inherit it.
   */
  entity: EntityPropType,
  /**
   * Dialog entity schema.
   * All the actions will inherit it.
   */
  schema: PropTypes.any,
  /**
   * Dialog title
   */
  title: PropTypes.string,
  /**
   * Adds spinner to dialog
   */
  loading: PropTypes.bool,
  /**
   * Called when dialog finished the action with success and closes.
   */
  onFinish: PropTypes.func,
  /**
   * Called when dialog is closed
   */
  onClose: PropTypes.func,
  /**
   * Children node
   */
  children: PropTypes.node,
};

export default ModelDialog;
